import {
  Typography,
  type TypographyPropsVariantOverrides,
} from '@mui/material';
import type { OverridableStringUnion } from '@mui/types';
import {
  type CSSProperties,
  type ElementType,
  useEffect,
  useReducer,
} from 'react';

import i18next from '~/common/configs/i18n.config';

// TODO(i18n): `TranslatedLabel` + `useI18nextRerender` below are a near-verbatim
// copy of `@gm-commercial/translations`'s `TranslatedLabel`. They're kept local
// so `translateText`/`TranslatedLabel` bind to `~/common/configs/i18n.config`
// (which guarantees i18next `init` runs on import). Future cleanup (also requires
// adding `@gm-commercial/translations` as a dependency of this app): collapse this
// file into a barrel that re-exports `TranslatedLabel`/`translateText` from
// `@gm-commercial/translations` (keeping the
// `i18n.config` side-effect import plus the local `translateObject`/
// `translationExists` helpers). Until then, keep the component and
// `useI18nextRerender` here in sync with the package copy.
export interface TranslationVariables {
  [key: string]: string | number | undefined;
}

interface FormatLabelProps {
  translationKey: string;
  variables?: TranslationVariables;
  typographyVariant?:
    | OverridableStringUnion<'inherit', TypographyPropsVariantOverrides>
    | undefined;
  style?: CSSProperties;
  className?: string;
  dataTestId?: string;
  component?: ElementType;
}

export const translateText = (
  key: string,
  variables?: TranslationVariables
): string => {
  return i18next.t(key, variables);
};

/**
 * Translate an i18n key that returns a structured object or array instead of a string.
 * This sets returnObjects=true internally, allowing you to access nested translation
 * structures as typed TypeScript objects.
 */
export function translateObject<T>(
  key: string,
  variables?: TranslationVariables
): T {
  return i18next.t(key, { ...variables, returnObjects: true }) as T;
}

/**
 * Returns whether a translation key exists for the current language.
 * @param key - The translation key to check (e.g. catalog:planDetail.OSCP.descriptionTooltip)
 * @returns true if the key exists, false otherwise
 */
export const translationExists = (key: string): boolean => i18next.exists(key);

/**
 * Re-renders the caller on i18next `languageChanged` events. translateText/
 * i18next.t are not reactive on their own, and i18next emits no event for
 * interpolation-default changes (those are surfaced by re-emitting
 * `languageChanged`), so we subscribe to the shared singleton directly.
 */
const useI18nextRerender = (): void => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    i18next.on('languageChanged', rerender);
    return () => {
      i18next.off('languageChanged', rerender);
    };
  }, []);
};

const TranslatedLabel = ({
  translationKey,
  variables,
  typographyVariant,
  style,
  className,
  dataTestId,
  component = 'span',
}: FormatLabelProps) => {
  useI18nextRerender();
  return (
    <Typography
      variant={typographyVariant}
      className={className}
      style={style}
      data-testid={dataTestId}
      component={component}
    >
      {i18next.t<string>(translationKey, variables)}
    </Typography>
  );
};

export default TranslatedLabel;
